import React, { useEffect, useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import { Menu } from "@headlessui/react";
import { HiOutlinePlus } from "react-icons/hi2";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import InputComponent from "../../../components/Tenant/InputComponent/InputComponent";
import {
  useAddVersionMutation,
  useDeleteVersionMutation,
  useGetAllVersionMutation,
  useUpdateHWSandSWSMutation,
} from "../../../features/tenant/AppVersions/AppVersionApi";
import toast from "react-hot-toast";

const AppVersion = () => {
  const primaryColor = JSON.parse(sessionStorage.getItem("vendorTheme")).theme.color_shades;

  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [version, setVersion] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [myList, setMyList] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedPlatform, setSelectedPlatform] = useState("android");

  const initialRequest = { limit: 10, offset: 0, platform: selectedPlatform };
  const [request, setRequest] = useState(initialRequest);

  const [AddVersion, { data: AddVersionData, error: AddVersionError }] = useAddVersionMutation();
  const [DeleteVersion, { data: DeleteVersionData, error: DeleteVersionError }] = useDeleteVersionMutation();
  const [UpdateHWSandSWS, { data: UpdateHWSandSWSData, error: UpdateHWSandSWSError }] = useUpdateHWSandSWSMutation();
  const [getAllVersion, { data: getAllVersionData }] = useGetAllVersionMutation();

  // Fetch versions
  useEffect(() => {
    getAllVersionFunction();
  }, [limit, offset, selectedPlatform]);

  // Handle Add success/error
  useEffect(() => {
    if (AddVersionData) {
      toast.success(AddVersionData.message);
      getAllVersionFunction();
      setShowModal(false);
    }
    if (AddVersionError) {
      toast.error(AddVersionError?.data?.message || "Error adding version");
    }
  }, [AddVersionData, AddVersionError]);

  // Handle Delete success/error
  useEffect(() => {
    if (DeleteVersionData) {
      toast.success(DeleteVersionData.message);
      getAllVersionFunction();
    }
    if (DeleteVersionError) {
      toast.error(DeleteVersionError?.data?.message || "Error deleting version");
    }
  }, [DeleteVersionData, DeleteVersionError]);

  // Handle HWV/SWV update success/error
  useEffect(() => {
    if (UpdateHWSandSWSData) {
      toast.success(UpdateHWSandSWSData.message);
      getAllVersionFunction();
    }
    if (UpdateHWSandSWSError) {
      toast.error(UpdateHWSandSWSError?.data?.message || "Error updating version");
    }
  }, [UpdateHWSandSWSData, UpdateHWSandSWSError]);

  // On API data update
  useEffect(() => {
    if (getAllVersionData) {
      setMyList(getAllVersionData.body?.data || []);
      setTotalRows(getAllVersionData.body?.total || 0);
    }
  }, [getAllVersionData]);

  // API Call Function
  const getAllVersionFunction = () => {
    const req = { ...request, limit, offset, platform: selectedPlatform };
    getAllVersion({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      body: req,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!version.trim()) {
      toast.error("Please enter a version");
      return;
    }
    AddVersion({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      body: { version, platform: selectedPlatform },
    });
  };

  const handleDelete = (id) => {
    DeleteVersion({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      id,
    });
  };

  const handleUpdateHWVandSWV = (id, type) => {
    UpdateHWSandSWS({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      id,
      body: { type },
    });
  };

  const handlePageChange = (pageNumber) => setOffset((pageNumber - 1) * limit);
  const handlePerRowsChange = (newPerPage) => setLimit(newPerPage);

  const columns = [
    { name: "ID", selector: (row) => row.id, width: "10%" },
    { name: "Version", selector: (row) => row.version, width: "20%" },
    {
      name: "Highest Working Version",
      cell: (row) => (
        <button
          onClick={() => handleUpdateHWVandSWV(row.id, "hwv")}
          className={`px-3 py-1.5 rounded text-xs font-medium text-white ${
            row.hwv ? "bg-green-700" : "bg-gray-600"
          }`}
        >
          {row.hwv ? "Active" : "Inactive"}
        </button>
      ),
      width: "20%",
    },
    {
      name: "Lowest Working Version",
      cell: (row) => (
        <button
          onClick={() => handleUpdateHWVandSWV(row.id, "swv")}
          className={`px-3 py-1.5 rounded text-xs font-medium text-white ${
            row.swv ? "bg-green-700" : "bg-gray-600"
          }`}
        >
          {row.swv ? "Active" : "Inactive"}
        </button>
      ),
      width: "20%",
    },
    {
      name: "Action",
      cell: (row) => (
        <button
          className="bg-red-600 px-3 py-1.5 text-xs font-medium text-white rounded hover:bg-red-700"
          onClick={() => handleDelete(row.id)}
        >
          Delete
        </button>
      ),
      width: "10%",
    },
    { name: "Created", selector: (row) => row.created, width: "20%" },
  ];

  return (
    <div className="flex flex-col w-full h-full bg-gray-100">
      {/* Page Header */}
      <div className="border-b bg-white border-gray-300 px-6 py-2 shadow-sm text-xl font-bold flex justify-between items-center">
        <div>
          App Versions
          <LinksComponent />
        </div>
      </div>

      {/* Topbar Platform Filter */}
      <div className="bg-white shadow-sm border-b flex items-center justify-start px-6 py-3 space-x-4">
        <span className="font-medium text-gray-700 text-sm">Select Platform:</span>
        <button
          onClick={() => setSelectedPlatform("android")}
          className={`px-4 py-2 text-sm font-medium rounded-lg border transition ${
            selectedPlatform === "android"
              ? "bg-blue-600 text-white border-blue-600"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          Android
        </button>
        <button
          onClick={() => setSelectedPlatform("ios")}
          className={`px-4 py-2 text-sm font-medium rounded-lg border transition ${
            selectedPlatform === "ios"
              ? "bg-blue-600 text-white border-blue-600"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          iOS
        </button>
      </div>

      {/* Data Table */}
      <div className="flex-1 p-4">
        <div className="bg-white rounded-lg shadow p-4">
          <DataTableComponent
            keyField={(row) => row.id}
            columns={columns}
            data={myList}
            totalRows={totalRows}
            handlePerRowsChange={handlePerRowsChange}
            handlePageChange={handlePageChange}
          />
        </div>
      </div>

      {/* Floating Add Button */}
      <div className="fixed bottom-6 right-10">
        <Menu as="div" className="relative inline-block text-left">
          <Menu.Button
            onClick={() => setShowModal(true)}
            className="flex justify-center items-center w-14 h-14 rounded-full text-white shadow-lg hover:scale-105 transition-transform"
            style={{ backgroundColor: primaryColor["700"] }}
          >
            <HiOutlinePlus className="w-6 h-6" />
          </Menu.Button>
        </Menu>
      </div>

      {/* Add Version Modal */}
      {showModal && (
        <ModalComponent size="sm" show={showModal} setShow={setShowModal}>
          <div className="w-full p-4">
            <div className="mb-4 text-center font-semibold text-lg">
              Add Version
            </div>

            {/* Platform Selector */}
            <div className="flex justify-center space-x-4 mb-4">
              <button
                type="button"
                onClick={() => setSelectedPlatform("android")}
                className={`px-4 py-2 rounded-md text-sm font-medium border transition ${selectedPlatform === "android"
                    ? "bg-green-600 text-white border-green-600"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                Android
              </button>
              <button
                type="button"
                onClick={() => setSelectedPlatform("ios")}
                className={`px-4 py-2 rounded-md text-sm font-medium border transition ${selectedPlatform === "ios"
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                iOS
              </button>
            </div>

            {/* Version Input */}
            <InputComponent
              name="Enter Version*"
              placeholder="Version"
              type="text"
              value={version}
              setValue={setVersion}
              required
            />

            {/* Submit */}
            <div className="flex justify-center mt-4">
              <button
                onClick={handleSubmit}
                className="bg-blue-700 text-white px-5 py-2.5 rounded hover:bg-blue-800"
              >
                Add
              </button>
            </div>
          </div>
        </ModalComponent>
      )}

    </div>
  );
};

export default AppVersion;
